import uamClientService from '../uam/uamClientService';
import uamWebSocketResponseService from '../uam/uamWebSocketResponseService';
import IotDeviceWebSocketClient from './iotDeviceWebSocketClient';
import IotHolderWebSocketClient from './iotHolderWebSocketClient';
import uamTimeouts from '../../consts/uam/uamTimeouts';
import {selectUamAssetId} from '../../state/selectors/uamData';
import {selectIsHolderConnected} from '../../state/selectors/mwDevice';
import httpStatusTypesService from '../server/httpStatusTypesService';
import {onWebSocketDisconnect} from '../device/connectionService';
import helpers from '../../utils/helpers';
import {selectIsUamWebSocketAlive} from '../../state/selectors/iotData';
import {setUamWebSocketAlive} from '../../state/slices/iotData';
import {log} from '../log';

let uamDevicePingTimeout;
let uamHolderPingTimeout;
const clearPingTimeout = (isHolder) => clearTimeout(isHolder ? uamHolderPingTimeout : uamDevicePingTimeout);

let deviceReconnectTimeout;
let holderReconnectTimeout;
const clearReconnectTimeout = (isHolder) => clearTimeout(isHolder ? holderReconnectTimeout : deviceReconnectTimeout);

let isSocketClosed = true;

const getWebSocketClient = (isHolder) => (assetId, createNew, options) => {
    const Client = isHolder ? IotHolderWebSocketClient : IotDeviceWebSocketClient;

    return new Client(assetId, createNew, options);
};

const initWebSocket = async (isHolder, isReconnect) => {
    if (isReconnect && isSocketClosed) return;

    const assetId = selectUamAssetId(isHolder);

    if (!assetId) throw new Error('AssetId is not provided');

    if (isHolder) {
        const isHolderConnected = selectIsHolderConnected();

        if (!isHolderConnected) return;
    }

    isSocketClosed = false;

    clearReconnectTimeout(isHolder);

    if (isWebSocketConnected(isHolder)) return;

    const onError = (isReconnectAvailable, timer = uamTimeouts.UAM_WS_RECONNECT_TIMEOUT) => {
        //for now reconnect only if network error
        if (!isReconnectAvailable) {
            onWebSocketDisconnect(isHolder);
            return;
        }

        if (isReconnect && isSocketClosed) return;

        disconnectWebSocket(!!isHolder, false);

        const timeout = setTimeout(() => initWebSocket(isHolder, true), timer * 1000);

        if (isHolder) {
            holderReconnectTimeout = timeout;
        } else {
            deviceReconnectTimeout = timeout;
        }
    };

    const topicSubscribeSettings = await getTopicSubscribeSettings(assetId, isHolder, onError);

    if (!topicSubscribeSettings) return onError();

    if (isReconnect && isSocketClosed) return;

    const client = getWebSocketClient(isHolder)(assetId, true, topicSubscribeSettings);

    client.attachCloseHandler(async (message) => {
        switch (message?.code) {
            case 1000:
                //normal close from code
                onWebSocketDisconnect(isHolder);
                break;
            case 1003:
                onError();
                break;
            default:
                onError();
                break;
        }
    });

    client.attachConnectHandler(() => {
        subscribeOnMessage(isHolder);

        setupWebSocketPing(isHolder);
    });
};

const setupWebSocketPing = (isHolder) => {
    if (isSocketClosed) return;

    clearPingTimeout(isHolder);
    const timeout = setTimeout(async () => {
        await checkSocketAlive(isHolder);

        setupWebSocketPing(isHolder);
    }, uamTimeouts.UAM_WS_PING_INTERVAL * 1000);

    if (isHolder) {
        uamHolderPingTimeout = timeout;
    } else {
        uamDevicePingTimeout = timeout;
    }
};

const checkSocketAlive = async (isHolder) => {
    if (isSocketClosed) return;

    const checkStatus = async (isHolder) => {
        if (!isWebSocketConnected(isHolder)) return true;

        const webSocketClient = getWebSocketClient(isHolder)();

        setUamWebSocketAlive(isHolder, false);

        log.info(`Sending PING for websocket. isHolder: ${isHolder}`);

        webSocketClient.ping();
        return await waitUntilSocketRespond(isHolder);
    };

    const isSocketAlive = await checkStatus(isHolder);

    if (!isSocketAlive) {
        disconnectWebSocket(isHolder, false);
    }
};

const waitUntilSocketRespond = async (isHolder, timer = uamTimeouts.UAM_WS_PING_ALIVE_INTERVAL) => {
    const isSocketAlive = selectIsUamWebSocketAlive(isHolder);

    if (isSocketAlive) return true;

    if (--timer < 0) {
        return false;
    }
    await helpers.timeout(1000);
    return await waitUntilSocketRespond(isHolder, timer);
};

const getTopicSubscribeSettings = async (assetId, isHolder, onError) => {
    try {
        return await uamClientService.assetSubscribe(assetId, isHolder);
    } catch (e) {
        const isNetworkError = httpStatusTypesService.isNetworkError(e.response?.status);

        if (isNetworkError) return onError(true);

        onError();
    }
};

const disconnectWebSocket = (isHolder, isForceDisconnect = true) => {
    if (isForceDisconnect) {
        isSocketClosed = true;
    }

    clearReconnectTimeout(isHolder);

    let webSocketClient;

    if (isHolder === undefined) {
        webSocketClient = new IotDeviceWebSocketClient();
        webSocketClient.disconnectClient();
        webSocketClient = new IotHolderWebSocketClient();
        webSocketClient.disconnectClient();
    } else {
        webSocketClient = getWebSocketClient(isHolder)();
        webSocketClient.disconnectClient();
    }
};

/*const reconnectWebSockets = () => {
    const deviceWebSocketClient = new IotDeviceWebSocketClient();
    const holderWebSocketClient = new IotHolderWebSocketClient();

    deviceWebSocketClient.disconnectWithReconnectClient();
    holderWebSocketClient.disconnectWithReconnectClient();
};*/

const subscribeOnMessage = (isHolder) => {
    getWebSocketClient(isHolder)().attachMessageHandler(async (message) => {
        await uamWebSocketResponseService.proceedMessage(message, isHolder);
    });
};

const isWebSocketConnected = (isHolder) => {
    const assetId = selectUamAssetId(isHolder);
    const webSocketClient = getWebSocketClient(isHolder)();

    return webSocketClient.isConnectedCheck(assetId);
};

export default {
    initWebSocket,
    disconnectWebSocket,
    isWebSocketConnected,
    //checkSocketAlive,
};
